import React, { useState, useEffect } from 'react';
import { 
  Watch, Heart, Moon, Activity, Zap, 
  RefreshCw, Link2, Check, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';

const WearableCard = ({ name, icon, connected, onSync }) => {
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await onSync(name.toLowerCase().replace(' ', '_'));
      toast.success(`${name} synced successfully`);
    } catch (error) {
      toast.error(`Failed to sync ${name}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="hpme-card p-5">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-zinc-800 rounded-full flex items-center justify-center">
            {icon}
          </div>
          <div>
            <h3 className="font-secondary font-bold">{name}</h3>
            <span className={`text-xs ${connected ? 'text-green-500' : 'text-zinc-500'}`}>
              {connected ? 'Connected' : 'Not connected'}
            </span>
          </div>
        </div>
        {connected && <Check className="w-5 h-5 text-green-500" />}
      </div>
      <Button 
        className={connected ? "hpme-btn-secondary w-full" : "hpme-btn-primary w-full"}
        onClick={handleSync}
        disabled={syncing}
        data-testid={`sync-${name.toLowerCase().replace(' ', '-')}`}
      >
        {syncing ? 'Syncing...' : connected ? 'Sync Data' : 'Connect'}
      </Button>
    </div>
  );
};

const Wearables = () => {
  const [wearable, setWearable] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [latestRes, historyRes] = await Promise.all([
        api.getLatestWearable().catch(() => ({ data: null })),
        api.getWearableHistory().catch(() => ({ data: [] }))
      ]);
      setWearable(latestRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching wearable data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (source) => {
    const response = await api.syncWearable(source);
    setWearable(response.data);
    setHistory([response.data, ...history]);
  };

  const devices = [
    { name: 'Apple Watch', icon: <Watch className="w-6 h-6 text-white" /> },
    { name: 'Garmin', icon: <Activity className="w-6 h-6 text-blue-400" /> },
    { name: 'Whoop', icon: <Zap className="w-6 h-6 text-yellow-500" /> },
    { name: 'Oura', icon: <Moon className="w-6 h-6 text-purple-400" /> },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight">
              WEARABLE <span className="text-primary">INTEGRATION</span>
            </h1>
            <p className="text-zinc-400 mt-1">Connect and sync your fitness devices</p>
          </div>
        </div>

        {/* Coming Soon Notice */}
        <div className="bg-zinc-800/50 border border-zinc-700 p-4 rounded-sm flex items-start gap-3">
          <Watch className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-primary font-semibold">Coming Soon</p>
            <p className="text-zinc-400 text-sm">
              Wearable integration with Apple Health, Garmin Connect, Whoop, and Oura is on our roadmap. 
              For now, you can manually input your vitals (HRV, sleep, resting HR) on the Recovery page.
            </p>
          </div>
        </div>

        {/* Device Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {devices.map(device => (
            <WearableCard
              key={device.name}
              name={device.name}
              icon={device.icon}
              connected={wearable?.source === device.name.toLowerCase().replace(' ', '_')}
              onSync={handleSync}
            />
          ))}
        </div>

        {/* Latest Data */}
        {wearable && (
          <div className="space-y-6">
            <div className="hpme-card p-6">
              <div className="hpme-card-corner" />
              <h3 className="font-secondary font-bold text-xl mb-4">LATEST SYNC DATA</h3>
              <p className="text-xs text-zinc-500 mb-4">
                Source: {wearable.source} | {new Date(wearable.timestamp).toLocaleString()}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                  <Heart className="w-6 h-6 text-red-400 mx-auto mb-2" />
                  <p className="hpme-number text-3xl text-red-400">{wearable.heart_rate}</p>
                  <p className="text-xs text-zinc-500 uppercase mt-1">Heart Rate</p>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                  <Activity className="w-6 h-6 text-primary mx-auto mb-2" />
                  <p className="hpme-number text-3xl text-primary">{wearable.hrv?.toFixed(1)}</p>
                  <p className="text-xs text-zinc-500 uppercase mt-1">HRV (ms)</p>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                  <Moon className="w-6 h-6 text-accent-blue mx-auto mb-2" />
                  <p className="hpme-number text-3xl text-accent-blue">{wearable.sleep_hours?.toFixed(1)}</p>
                  <p className="text-xs text-zinc-500 uppercase mt-1">Sleep (hrs)</p>
                </div>
                <div className="bg-zinc-800/50 p-4 rounded-sm text-center">
                  <Zap className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
                  <p className="hpme-number text-3xl text-yellow-500">{wearable.recovery_score?.toFixed(0)}</p>
                  <p className="text-xs text-zinc-500 uppercase mt-1">Recovery</p>
                </div>
              </div>
            </div>

            {/* Additional Metrics */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="hpme-card p-6">
                <div className="hpme-card-corner" />
                <h3 className="font-secondary font-bold text-xl mb-4">DAILY ACTIVITY</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-zinc-400">Steps</span>
                      <span className="hpme-number text-primary">{wearable.steps?.toLocaleString()}</span>
                    </div>
                    <Progress value={(wearable.steps / 10000) * 100} className="h-2 bg-zinc-800" />
                    <p className="text-xs text-zinc-500 mt-1">Goal: 10,000</p>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-zinc-400">Calories</span>
                      <span className="hpme-number text-accent-blue">{wearable.calories_burned?.toLocaleString()}</span>
                    </div>
                    <Progress value={(wearable.calories_burned / 2500) * 100} className="h-2 bg-zinc-800" />
                    <p className="text-xs text-zinc-500 mt-1">Goal: 2,500</p>
                  </div>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-zinc-400">Strain Score</span>
                      <span className="hpme-number text-accent-purple">{wearable.strain_score?.toFixed(1)}</span>
                    </div>
                    <Progress value={(wearable.strain_score / 21) * 100} className="h-2 bg-zinc-800" />
                    <p className="text-xs text-zinc-500 mt-1">Max: 21</p>
                  </div>
                </div>
              </div>

              <div className="hpme-card p-6">
                <div className="hpme-card-corner" />
                <h3 className="font-secondary font-bold text-xl mb-4">SLEEP STAGES</h3>
                {wearable.sleep_stages && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-zinc-400">Deep Sleep</span>
                        <span className="hpme-number text-accent-purple">{wearable.sleep_stages.deep?.toFixed(1)}h</span>
                      </div>
                      <Progress value={(wearable.sleep_stages.deep / 2) * 100} className="h-2 bg-zinc-800" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-zinc-400">Light Sleep</span>
                        <span className="hpme-number text-accent-blue">{wearable.sleep_stages.light?.toFixed(1)}h</span>
                      </div>
                      <Progress value={(wearable.sleep_stages.light / 4) * 100} className="h-2 bg-zinc-800" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-zinc-400">REM Sleep</span>
                        <span className="hpme-number text-primary">{wearable.sleep_stages.rem?.toFixed(1)}h</span>
                      </div>
                      <Progress value={(wearable.sleep_stages.rem / 2) * 100} className="h-2 bg-zinc-800" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="text-zinc-400">Awake</span>
                        <span className="hpme-number text-yellow-500">{wearable.sleep_stages.awake?.toFixed(1)}h</span>
                      </div>
                      <Progress value={(wearable.sleep_stages.awake / 1) * 100} className="h-2 bg-zinc-800" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* No Data State */}
        {!wearable && !loading && (
          <div className="hpme-card p-12 text-center">
            <Watch className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <h3 className="font-secondary font-bold text-xl mb-2">NO WEARABLE DATA</h3>
            <p className="text-zinc-400 mb-6">Connect a device above to sync your health data</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Wearables;
